import { useState } from "react";

import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, ClockIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";

import { TicketSort } from "@/features/tickets/types/filters";
import { cn } from "@/shared/lib/utils/cn";

interface Props {
  sort: TicketSort;
  onApply: (sort: TicketSort) => void;
}

const SORT_OPTIONS = [
  { label: "Date created", sortBy: "created" },
  { label: "Last updated", sortBy: "updated" },
  { label: "Priority", sortBy: "priority" },
  { label: "Title", sortBy: "title" },
] as const;

export function SortPopover({ sort, onApply }: Props) {
  const [open, setOpen] = useState(false);
  const [draftSortBy, setDraftSortBy] = useState(sort.sortBy);
  const [draftDirection, setDraftDirection] = useState(sort.sortDirection);

  function handleOpenChange(next: boolean) {
    if (next) {
      setDraftSortBy(sort.sortBy);
      setDraftDirection(sort.sortDirection);
    }
    setOpen(next);
  }

  function handleApply() {
    onApply({ sortBy: draftSortBy, sortDirection: draftDirection });
    setOpen(false);
  }

  const activeLabel = SORT_OPTIONS.find((o) => o.sortBy === sort.sortBy)?.label ?? "Date created";

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 text-xs font-medium text-muted-foreground">
          <ClockIcon className="size-3.5 shrink-0" />
          {activeLabel}
          <ChevronDownIcon className="size-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-48 gap-0 p-0">
        <p className="px-3 py-2 text-xs font-semibold tracking-wider text-muted-foreground uppercase">Sort by</p>
        <Separator />
        <div className="flex flex-col py-1">
          {SORT_OPTIONS.map((option) => (
            <button key={option.sortBy} onClick={() => setDraftSortBy(option.sortBy)} className={cn("px-3 py-2 text-left text-sm transition-colors hover:bg-accent", draftSortBy === option.sortBy && "font-medium text-primary")}>
              {option.label}
            </button>
          ))}
        </div>
        <Separator />
        <div className="flex flex-col gap-1.5 px-3 py-2">
          <p className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">Direction</p>
          <div className="flex gap-1">
            <button onClick={() => setDraftDirection("desc")} className={cn("flex items-center gap-1 px-2 py-1 text-xs transition-colors hover:bg-accent", draftDirection === "desc" && "bg-accent font-medium text-primary")}>
              <ArrowDownIcon className="size-3" />
              Desc
            </button>
            <button onClick={() => setDraftDirection("asc")} className={cn("flex items-center gap-1 px-2 py-1 text-xs transition-colors hover:bg-accent", draftDirection === "asc" && "bg-accent font-medium text-primary")}>
              <ArrowUpIcon className="size-3" />
              Asc
            </button>
          </div>
        </div>
        <Separator />
        <div className="flex justify-end px-3 py-2">
          <Button size="sm" className="h-7 text-xs" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
